<template>
  <BittsModal
    aria-label="Add more seats to your Connector plan"
    :loading
    :width="1000"
    @closed="onClosed"
    class="add-seats-modal"
    :hide-footer="true"
  >
    <template #header>
      <h1>Add more seats to your Connector plan</h1>
    </template>
    <div class="grid grid-cols-[3fr_2fr] gap-40">
      <ul class="flex flex-col pt-40 pb-16 gap-24">
        <li class="flex flex-col gap-16">
          <h3>1. Additional Seats</h3>
          <SeatTypeCard
            :key="seatCardProps.title"
            v-bind="seatCardProps"
            class="w-full"
            @update:seat-count="(count, _) => (addedCoreSeats = count)"
          />
        </li>
        <li class="flex flex-col gap-16">
          <h3>{{
            `2. ${editCard ? 'Edit Payment Method' : 'Payment Method'}`
          }}</h3>
          <div v-if="!editCard" class="add-seats-modal__edit-card-input">
            <div>
              <FontAwesomeIcon
                :icon="['fak', 'credit-card']"
                class="text-neutral-text-placeholder text-16"
              />
              <span class="ml-6 text-neutral-text-weak">{{
                creditCardText
              }}</span>
            </div>
            <button
              data-testid="edit-card-button"
              class="add-seats-modal__edit-button"
              @click="() => (editCard = true)"
              type="button"
            >
              Edit
            </button>
          </div>
          <EditPaymentMethod
            v-else
            :credit-card-text
            @return-to-summary="() => (editCard = false)"
          />
        </li>
      </ul>
      <CostSummaryNew
        :added-seats="addedCoreSeats"
        :billing-period
        :core-seat-limit
        :subscription-next-billing-date="String(subscriptionNextBillingDate)"
        class="mt-40"
        @amount-due-today="
          (amountDue) => {
            amountDueToday = amountDue;
          }
        "
        @add-seats="onAddSeats"
        @cancel="onClosed"
    /></div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsModal } from '@crossbeam/bitts';
import { EVENT_SITES, PLAN_AND_BILLING_CTAS } from '@crossbeam/itly';

import { capitalize } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import CostSummaryNew from '@/components/billing/CostSummaryNew.vue';
import EditPaymentMethod from '@/components/billing/EditPaymentMethod.vue';
import SeatTypeCard from '@/components/billing/SeatTypeCard.vue';

import useBilling from '@/composables/useBilling';
import useIteratively from '@/composables/useIteratively';
import useSeats from '@/composables/useSeats';
import { CORE_SEAT_COMPONENTS, FULL_ACCESS } from '@/constants/billing';
import { useBillingStore } from '@/stores';
import { PostSubscriptionComponent } from '@/types/billing';

const emit = defineEmits<(e: 'closed') => void>();
const loading = ref(false);

const {
  billingPeriod,
  creditCardType,
  creditCardLastFourDigits,
  subscriptionNextBillingDate,
  subscriptionType,
} = storeToRefs(useBillingStore());

const { purchaseComponents } = useBilling();

const { coreSeatLimit } = useSeats();

const seatCardProps = {
  title: 'Full Access Seats',
  description:
    'Build a predictable Ecosystem-Led Growth revenue engine with complete access to Crossbeam and admin controls',
  type: FULL_ACCESS,
};

const addedCoreSeats = ref(1);

const creditCardText = computed(() => {
  return `${capitalize(creditCardType.value)} (•••• ${creditCardLastFourDigits.value})`;
});

const editCard = ref(false);

const { iteratively } = useIteratively();
const amountDueToday = ref(0);
const coreHandle = CORE_SEAT_COMPONENTS[subscriptionType.value as string];
async function onAddSeats() {
  loading.value = true;

  const components: PostSubscriptionComponent[] = [
    {
      quantity: coreSeatLimit.value + addedCoreSeats.value,
      component_handle: coreHandle || '',
    },
  ];

  await purchaseComponents(components, addedCoreSeats.value);
  iteratively.userClickedPayNow({
    cta: PLAN_AND_BILLING_CTAS.ADD_SEATS,
    event_site: EVENT_SITES.ADD_SEATS_MODAL,
    billing_cycle: billingPeriod.value,
    full_seats: addedCoreSeats.value,
    sales_seats: 0,
    invoice_amount: amountDueToday.value,
  });
  emit('closed');
  loading.value = false;
}

function onClosed() {
  iteratively.userClickedKeepCurrentPlan({
    cta: PLAN_AND_BILLING_CTAS.ADD_SEATS,
    event_site: EVENT_SITES.ADD_SEATS_MODAL,
  });
  emit('closed');
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
h1 {
  @apply text-xl font-bold text-neutral-text-strong;
}
h3 {
  @apply text-neutral-text-weak;
}
.o-bitts-modal.bitts-modal--primary:deep() .o-bitts-modal__header {
  @apply items-start p-24 border-b border-solid border-neutral-border;
}
.add-seats-modal__edit-card-input {
  @apply flex items-center justify-between border border-neutral-border shadow-component rounded-8 pl-12 pr-8 py-6 max-w-[340px] mb-24;
  .add-seats-modal__edit-button {
    @apply text-neutral-text-button text-sm font-bold hover:bg-neutral-100 px-8 py-4 rounded-4;
  }
}
.o-bitts-modal:deep() dialog .o-bitts-modal__content {
  @apply overflow-y-visible;
}
:deep(iframe) {
  width: 100% !important;
}
:deep(.o-bitts-modal__content .c-loading) {
  @apply min-h-[400px];
}
</style>
